import { SeoController } from '@application/components';
import { PRO_PAGE_ROUTE_PER_PRO_TYPE } from '@application/constants';
import { useProSearch, useRouter } from '@application/utils';
import type { ProLandingGetGroomersQuery, ProLandingGetVetsQuery } from '@gql/graphql';
import { ProfessionalTypeEnum } from '@gql/graphql';
import type { CardItemProSmallData } from '@presentation/components';
import { PLACEHOLDER_IMAGE } from '@presentation/constants';
import { ProLandingPage } from '@presentation/pages';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useGetGroomers } from './api/useGetGroomers';
import { useGetVets } from './api/useGetVets';

const REMOVED_PREFIXES = [
  'Centre',
  'Vétérinaire',
  'Cabinet',
  'Dierenartsenpraktijk',
  'Dierenarts centrum',
  'Salon de Toilettage',
  'Dierenartsenpraktijk',
  'Hondenkapsalon',
];

type Professional =
  | ProLandingGetVetsQuery['searchProfessionals']['professionals'][number]
  | ProLandingGetGroomersQuery['searchProfessionals']['professionals'][number];

export const ProLandingPageController: FC = () => {
  const { translate, navigateToRoute, translatedRoute } = useRouter();

  const { vets, vetsLoading } = useGetVets();
  const { groomers, groomersLoading } = useGetGroomers();
  const proSearch = useProSearch();

  const formatTitle = useCallback(
    (pro: Professional): string => {
      if (pro.type === ProfessionalTypeEnum.VET) {
        return `${translate('glossary.vet')} ${pro.namePro || pro.mainProLastName}`;
      }
      let newTitle = pro.translations[0]?.title || '';
      // eslint-disable-next-line no-restricted-syntax
      for (const prefix of REMOVED_PREFIXES) {
        newTitle = newTitle.replaceAll(`${prefix} `, '');
        newTitle = newTitle.replaceAll(prefix, '');
      }
      return newTitle;
    },
    [translate],
  );

  const formatItem = useCallback(
    (item: Professional): CardItemProSmallData => {
      return {
        image: item.imgs?.[0]?.small ?? PLACEHOLDER_IMAGE,
        title: formatTitle(item),
        subtitle: item.address?.city ?? '',
        onClick: () =>
          navigateToRoute(PRO_PAGE_ROUTE_PER_PRO_TYPE[item.type], {
            params: [item.translations[0].url ?? ''],
          }),
      };
    },
    [formatTitle, navigateToRoute],
  );

  const formattedVets = useMemo(() => {
    return vets.map((vet) => formatItem(vet));
  }, [formatItem, vets]);

  const formattedGroomers = useMemo(() => {
    return groomers.map((groomer) => formatItem(groomer));
  }, [formatItem, groomers]);

  return (
    <>
      <SeoController
        title={translate('proLanding.seo.title')}
        description={translate('proLanding.seo.description')}
        keywords={translate('proLanding.seo.keywords')}
      />
      <ProLandingPage
        vetItems={formattedVets}
        groomerItems={formattedGroomers}
        vetItemsLoading={vetsLoading}
        groomerItemsLoading={groomersLoading}
        joinProLink={translatedRoute('joinLanding')}
        moreVetsLink={translatedRoute('vetList')}
        moreGroomersLink={translatedRoute('vetList')}
        proSearch={proSearch}
      />
    </>
  );
};
